import { supabase } from '../config/supabaseClient.js';
import { sessao } from '../lib/auth.js';
import { operadorAtual } from '../lib/operador.js';
import { formatarNome, mascararMatricula } from '../lib/dom.js';
import { buscarTodos, orBuscaPessoa, orBuscaPessoaLegado, marcarHomonimos } from '../lib/db.js';

/** Registra movimentação. Grava a operadora (recepcionista) atual para atribuição. */
export async function registrarMovimentacao(mov) {
  const { error } = await supabase.from('movimentacao_chaves').insert({
    chave_id: mov.chave_id,
    colaborador_id: mov.colaborador_id || null,
    portador_avulso: mov.portador_avulso || null,
    motivo_id: mov.motivo_id || null,
    devolvido_por: mov.devolvido_por || null,
    operador_usuario_id: operadorAtual.id || null,
    tipo_movimentacao: mov.tipo_movimentacao,
    observacao: mov.observacao || null,
    usuario_registro_id: sessao.usuario.id,
  });
  if (error) {
    const msg = error.message || '';
    if (/Failed to fetch|NetworkError|network|fetch/i.test(msg))
      return { erro: 'Sem conexão — sua ação NÃO foi registrada. Tente de novo.', tipo: 'rede' };
    if (/j[áa] est[áa] RETIRADA/i.test(msg))
      return { erro: 'Esta chave acabou de ser emprestada (por outra recepção).', tipo: 'conflito' };
    if (/j[áa] est[áa] DISPONIVEL/i.test(msg))
      return { erro: 'Esta chave já foi devolvida.', tipo: 'conflito' };
    return { erro: 'Não foi possível registrar a movimentação: ' + msg, tipo: 'outro' };
  }
  return {};
}

export async function listarMotivos() {
  const { data, error } = await supabase.from('motivos').select('id, nome').eq('ativo', true).order('ordem');
  if (error) { console.error('[listarMotivos]', error.message); return []; }
  return data || [];
}

export async function buscarColaboradores(termo) {
  // ATIVOS + acesso suspenso (para avisar). Tolerante: cai do select completo p/ o simples,
  // e do filtro por colunas-digito (0044) p/ o padrao por blocos se a migration nao rodou.
  const tiers = [
    () => supabase.from('colaborador').select('id, nome, cpf, matricula, status, acesso_suspenso, suspensao_motivo, suspensao_ate, empregador:empregador_id ( empresa )').or('status.eq.ATIVO,acesso_suspenso.eq.true'),
    () => supabase.from('colaborador').select('id, nome, cpf, matricula, empregador:empregador_id ( empresa )').eq('status', 'ATIVO'),
    () => supabase.from('colaborador').select('id, nome, cpf, matricula').eq('status', 'ATIVO'),
  ];
  const filtros = termo ? [orBuscaPessoa(termo), orBuscaPessoaLegado(termo)] : [''];
  let resp = { data: null, error: true };
  for (const filtro of filtros) {
    for (const mk of tiers) {
      let q = mk();
      if (filtro) q = q.or(filtro);
      resp = await q.order('nome').limit(20);
      if (!resp.error) break;
    }
    if (!resp.error) break;
  }
  return marcarHomonimos(resp.data || []);
}

export async function listarChavesComStatus() {
  return buscarTodos(() => supabase
    .from('chave')
    .select('id, numero, descricao, situacao, emprestada_em, colaborador_atual_id, portador_avulso, ultima_retirada_em, ultima_retirada_por, ultima_devolucao_em, ultima_devolucao_por, ultima_retirada_operador, colaborador:colaborador_atual_id ( nome, matricula )')
    .eq('status', 'ATIVO')
    .order('descricao', { ascending: true }));
}

export async function historicoMovimentacoes(filtros = {}) {
  let q = supabase
    .from('movimentacao_chaves')
    .select(`
      id, tipo_movimentacao, data_hora_movimentacao, observacao, portador_avulso, devolvido_por,
      chave:chave_id ( numero, descricao ),
      colaborador:colaborador_id ( nome, matricula ),
      motivo:motivo_id ( nome ),
      operador:operador_id ( nome ),
      operador_usuario:operador_usuario_id ( colaborador:colaborador_id ( nome ) )
    `)
    .order('data_hora_movimentacao', { ascending: false })
    .limit(filtros.limite || 200);
  if (filtros.de) q = q.gte('data_hora_movimentacao', filtros.de);
  if (filtros.ate) q = q.lte('data_hora_movimentacao', filtros.ate);
  if (filtros.chave_id) q = q.eq('chave_id', filtros.chave_id);
  if (filtros.colaborador_id) q = q.eq('colaborador_id', filtros.colaborador_id);
  const { data } = await q;
  return (data || []).map((m) => ({
    id: m.id,
    tipo: m.tipo_movimentacao,
    data_hora: m.data_hora_movimentacao,
    chave: m.chave ? `${m.chave.numero}${m.chave.descricao ? ' — ' + m.chave.descricao : ''}` : '',
    colaborador: m.colaborador ? `${formatarNome(m.colaborador.nome)} (${mascararMatricula(m.colaborador.matricula)})` : (m.portador_avulso ? `${formatarNome(m.portador_avulso)} (avulso)` : ''),
    motivo: m.motivo?.nome || '',
    operador: formatarNome(m.operador_usuario?.colaborador?.nome || m.operador?.nome || '',),
    devolvido_por: formatarNome(m.devolvido_por || ''),
    observacao: m.observacao || '',
  }));
}


/**
 * IDs distintos das chaves que um colaborador RETIROU nos últimos 6 meses (mais recente primeiro).
 * Usado no filtro "Por colaborador" da movimentação.
 */
export async function chavesRecentesDoColaborador(colaboradorId) {
  const desde = new Date(); desde.setMonth(desde.getMonth() - 6);
  const { data, error } = await supabase.from('movimentacao_chaves')
    .select('chave_id, data_hora_movimentacao')
    .eq('colaborador_id', colaboradorId)
    .eq('tipo_movimentacao', 'RETIRADA')
    .gte('data_hora_movimentacao', desde.toISOString())
    .order('data_hora_movimentacao', { ascending: false })
    .limit(500);
  if (error) { console.error('[chavesRecentesDoColaborador]', error.message); return []; }
  const vistos = new Set(); const ids = [];
  for (const m of (data || [])) { if (m.chave_id && !vistos.has(m.chave_id)) { vistos.add(m.chave_id); ids.push(m.chave_id); } }
  return ids;
}
